import { createRouter, createWebHashHistory, RouteRecordRaw } from "vue-router";
import store from "@/store";
import { Mutations, Actions } from "@/store/enums/StoreEnums";
import JwtService from "@/core/services/JwtService";
import ApiService from "@/core/services/ApiService";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "sign-in",
    component: () => import("@/components/page-layouts/SignupBackground.vue"),
    children: [
      {
        path: "/sign-up",
        name: "signupForm",
        component: () => import("@/views/SignupForm.vue"),
      },
      {
        path: "/validate-email",
        name: "validateEmail",
        component: () => import("@/views/EmailValidationStaticPage.vue"),
      },
      {
        path: "/step-form",
        name: "stepForm",
        component: () => import("@/views/StepForm.vue"),
      },
      {
        path: "/welcome-dashboard",
        name: "welcomeDashboard",
        component: () => import("@/views/WelcomeDashboard.vue"),
      },
    ],
  },
  {
    path: "/",
    redirect: "/sign-in",
    component: () => import("@/components/page-layouts/Auth.vue"),
    children: [
      {
        path: "/sign-in",
        name: "sign-in",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignIn.vue"),
      },
      {
        path: "/mfa-set-up",
        name: "mfaSetUp",
        component: () => import("@/views/MfaSetupPage.vue"),
      },
      {
        path: "/mfa-verification",
        name: "mfaVerification",
        component: () => import("@/views/MfaVerificationPage.vue"),
      },
      {
        path: "/forgot-password",
        name: "forgotPassword",
        component: () => import("@/views/ForgotPassword.vue"),
      },
      {
        path: "/reset-password/:secret",
        name: "resetPassword",
        component: () => import("@/views/ResetPasswordForm.vue"),
      },
      {
        path: "/set-password/:secret",
        name: "setPassword",
        component: () => import("@/views/SetUpPasswordForm.vue"),
      },
      {
        path: "/direct-debit/complete",
        name: "directDebitComplete",
        component: () => import("@/views/DirectDebitComplete.vue"),
      },
      {
        path: "/welcome",
        name: "afterProcessPayment",
        component: () => import("@/views/AfterProcessingPaymentPage.vue"),
      },
    ],
  },
  {
    path: "/",
    redirect: "/clients/provisioning",
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/clients",
        name: "clients",
        component: () => import("@/views/Clients.vue"),
      },
      {
        path: "/bd-clients",
        name: "bd-clients",
        component: () =>
          import("@/components/customers/cards/overview/BdClients.vue"),
      },
      {
        path: "/inventory/SIM-cards",
        name: "sim-cards",
        component: () =>
          import("@/components/customers/cards/overview/SIMCards.vue"),
      },
      {
        path: "/inventory/devices",
        name: "bd-devices",
        component: () =>
          import("@/components/customers/cards/overview/AlarmDevices.vue"),
      },
      {
        path: "/clients/provisioning",
        name: "provisioning-clients",
        component: () => import("@/views/Customers.vue"),
      },
      {
        path: "/clients/provisioning/:id/:showComponent",
        name: "provisioning-clients-detail",
        component: () => import("@/views/apps/customers/CustomerDetails.vue"),
      },
      {
        path: "/dashboard/:id/:showComponent",
        name: "customer-dashboard",
        component: () => import("@/views/apps/customers/CustomerDetails.vue"),
      },
      {
        path: "/clients/provisioning/create/:customerId/:type",
        name: "new-provisioning-clients",
        component: () => import("@/views/UpdateProvisioningClientForm.vue"),
      },
      {
        path: "/client/:id",
        name: "client-single",
        component: () => import("@/views/ClientSingle.vue"),
      },
      {
        path: "/clients/provisioning/update/:customerId/:residentId",
        name: "edit-provisioning-clients",
        component: () => import("@/views/UpdateProvisioningClientForm.vue"),
      },
      {
        path: "/clients/provisioning/update/:residentId",
        name: "edit-provisioning-client",
        component: () => import("@/views/UpdateProvisioningClientForm.vue"),
      },
      {
        path: "/individual-provisioning-group/data-entry",
        name: "individual-provisioning-data-entry",
        component: () => import("@/views/DataEntryClients.vue"),
      },
      {
        path: "/individual-provisioning-group/awaiting-payment",
        name: "individual-provisioning-awaiting-payment",
        component: () => import("@/views/AwaitingPaymentClients.vue"),
      },
      {
        path: "/individual-provisioning-group/device-configuration",
        name: "individual-provisioning-device-configuration",
        component: () => import("@/views/DeviceConfigClients.vue"),
      },
      {
        path: "/individual-provisioning-group/awaiting-dispatch",
        name: "individual-provisioning-awaiting-dispatch",
        component: () => import("@/views/AwaitingDispatchClients.vue"),
      },
      {
        path: "/facility-provisioning-group/data-entry",
        name: "provisioning-data-entry",
        component: () => import("@/views/DataEntryFacilityClients.vue"),
      },
      {
        path: "/facility-provisioning-group/awaiting-payment",
        name: "facility-provisioning-awaiting-payment",
        component: () => import("@/views/AwaitingPaymentFacilityClients.vue"),
      },
      {
        path: "/facility-provisioning-group/device-configuration",
        name: "facility-provisioning-device-configuration",
        component: () => import("@/views/DeviceConfigFacilityClients.vue"),
      },
      {
        path: "/facility-provisioning-group/awaiting-dispatch",
        name: "facility-provisioning-awaiting-dispatch",
        component: () => import("@/views/AwaitingDispatchFacilityClients.vue"),
      },
      {
        path: "/billing-group/overdue-invoices",
        name: "overdue-invoices",
        component: () => import("@/views/OverdueCustomers.vue"),
      },
      {
        path: "/billing-group/failed-transactions",
        name: "failed-transaction-invoices",
        component: () => import("@/views/FailedTransactionCustomers.vue"),
      },
      {
        path: "/billing-group/expried-credit-cards",
        name: "expired-credit-card",
        component: () => import("@/views/ExpiredCreditCardCustomers.vue"),
      },
      {
        path: "/billing-group/nab-schedules",
        name: "nab-schedules",
        component: () => import("@/views/NabSchedules.vue"),
      },
      {
        path: "/invoices",
        name: "invoices-list",
        component: () => import("@/views/Invoices.vue"),
      },
      {
        path: "/devices",
        name: "devices",
        component: () => import("@/views/Devices.vue"),
      },
      {
        path: "/groups/villages",
        name: "groups-villages",
        component: () => import("@/views/Village.vue"),
      },
      {
        path: "/groups/villages/:id",
        name: "groups-villages-single",
        component: () => import("@/views/VillageSingle.vue"),
      },
      {
        path: "/groups/organisations",
        name: "groups-organisations",
        component: () => import("@/views/Organization.vue"),
      },
      {
        path: "/billing-config/independent",
        name: "billing-config-independent",
        component: () => import("@/views/IndependentBillingConf.vue"),
      },
      {
        path: "/billing-config/village",
        name: "billing-config-village",
        component: () => import("@/views/VillageBillingConf.vue"),
      },
      {
        path: "/billing",
        name: "billing",
        component: () => import("@/views/Billings.vue"),
      },
      {
        path: "/xero/contacts",
        name: "xero",
        component: () => import("@/views/Xero.vue"),
      },
      {
        path: "/provisioning",
        name: "provisioning",
        component: () => import("@/views/Dashboard.vue"),
      },
      {
        path: "/admin/users",
        name: "adminUser",
        component: () => import("@/views/admin/users/AdminUsers.vue"),
      },
      {
        path: "/admin/groups",
        name: "adminGroup",
        component: () => import("@/views/UserGroups.vue"),
      },
      {
        path: "/admin/accesses",
        name: "adminAccess",
        component: () => import("@/views/UserAccesses.vue"),
      },
      {
        path: "/admin/tools",
        name: "adminTool",
        component: () => import("@/views/ToolsTable.vue"),
      },
      {
        path: "/admin/subscription",
        name: "adminSubscription",
        component: () => import("@/views/SubscriptionManagement.vue"),
      },
      {
        path: "/admin/auto-billing-migration",
        name: "autoBillingMigration",
        component: () => import("@/views/AutoBillingMigration.vue"),
      },
      {
        path: "/admin/recurring-history",
        name: "adminRecurringHistory",
        component: () => import("@/views/RecurringHistoryTable.vue"),
      },
      {
        path: "/admin/scheduled-reports",
        name: "adminScheduledReports",
        component: () => import("@/views/AdminScheduledReports.vue"),
      },
      {
        path: "/admin/sms-settings",
        name: "adminSmsSettings",
        component: () => import("@/views/AdminSmsSettings.vue"),
      },
      {
        path: "/admin/gocardless",
        name: "adminGoCardless",
        component: () => import("@/views/AdminGoCardless.vue"),
      },
      {
        path: "/admin/recurring-billing",
        name: "adminRecurringBilling",
        component: () => import("@/views/AdminRecurringBilling.vue"),
      },
      {
        path: "/admin/logs",
        name: "adminLogs",
        component: () => import("@/views/HistoryTable.vue"),
      },
      {
        path: "/admin/subscription/rosiePlanItems/:rosiePlanId",
        name: "adminRosiePlan",
        component: () => import("@/views/RosiePlanItemsTable.vue"),
      },
      {
        path: "/user/profile",
        name: "userProfile",
        component: () => import("@/components/page-layouts/Profile.vue"),
      },
      {
        path: "/manager/user",
        name: "managerUser",
        component: () => import("@/components/widgets/tables/AllUserTable.vue"),
      },
      {
        path: "/test",
        name: "test",
        component: () =>
          import("@/components/tables/CustomerAlarmDeviceTable.vue"),
      },
    ],
  },
  {
    // Admin "View as customer": a new tab lands here, then shows My Rosie as that customer
    path: "/view-as",
    name: "view-as",
    component: () => import("@/views/portal/ViewAsStart.vue"),
  },
  {
    // Customer portal ("My Rosie") - the only pages a Rosie Customer sees once their account is active
    path: "/my-rosie",
    component: () => import("@/layout/portal/PortalLayout.vue"),
    redirect: "/my-rosie/home",
    children: [
      {
        path: "home",
        name: "portal-home",
        component: () => import("@/views/portal/PortalHome.vue"),
        meta: { portal: true },
      },
      {
        path: "family",
        name: "portal-family",
        component: () => import("@/views/portal/PortalFamily.vue"),
        meta: { portal: true },
      },
      {
        path: "family/:id",
        name: "portal-client",
        component: () => import("@/views/portal/PortalClient.vue"),
        meta: { portal: true },
      },
      {
        path: "billing",
        name: "portal-billing",
        component: () => import("@/views/portal/PortalBilling.vue"),
        meta: { portal: true },
      },
      {
        path: "payment",
        name: "portal-payment",
        component: () => import("@/views/portal/PortalPayment.vue"),
        meta: { portal: true },
      },
      {
        path: "account",
        name: "portal-account",
        component: () => import("@/views/portal/PortalAccount.vue"),
        meta: { portal: true },
      },
      // Client portal: a Rosie Client signed in with their own login (their details and bills only)
      {
        path: "me",
        name: "portal-client-home",
        component: () => import("@/views/portal/PortalClientHome.vue"),
        meta: { portal: true, client: true },
      },
      {
        path: "me/details",
        name: "portal-my-details",
        component: () => import("@/views/portal/PortalClient.vue"),
        meta: { portal: true, client: true },
      },
      {
        path: "me/bills",
        name: "portal-my-bills",
        component: () => import("@/views/portal/PortalBilling.vue"),
        meta: { portal: true, client: true },
      },
    ],
  },
  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "404",
    component: () => import("@/views/crafted/authentication/Error404.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});
/** Pages a Rosie Customer may open besides the portal (signing in, password reset ...). */
const PORTAL_USER_ALLOWED = new Set([
  "sign-in",
  "mfaSetUp",
  "mfaVerification",
  "forgotPassword",
  "resetPassword",
  "setPassword",
  "directDebitComplete",
  "404",
]);

router.beforeEach((to) => {
  store.commit(Mutations.RESET_LAYOUT_CONFIG);
  if (JwtService.getToken() !== null) {
    ApiService.setHeader();
    store.dispatch(Actions.VERIFY_AUTH);
    const currentStatus = String(store.getters.currentStatus || "");
    // Someone signing up online only sees the sign-up form, then the welcome page once they've paid
    // (until staff activate them for My Rosie). Redirect the navigation itself, so another page
    // never renders first. (The server only lets them use the sign-up APIs anyway.)
    const signingUpPages = currentStatus.startsWith("PENDING_SIGNUP")
      ? ["stepForm"]
      : currentStatus.startsWith("PROVISIONING_CUSTOMER")
      ? ["welcomeDashboard", "afterProcessPayment"]
      : null;
    if (
      signingUpPages &&
      !signingUpPages.includes(String(to.name)) &&
      !PORTAL_USER_ALLOWED.has(String(to.name))
    ) {
      return { name: signingUpPages[0] };
    }
    if (currentStatus.startsWith("ACTIVE") && to.name === "stepForm") {
      return { path: "/" };
    }
  }
  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 100);

  // Active Rosie Customers and Rosie Clients only use the portal (each their own part of it);
  // everyone else stays out of it. (The server enforces the same: customer portal endpoints are
  // Rosie Customer only, client portal endpoints Rosie Client only, admin endpoints neither.)
  if (JwtService.getToken() !== null) {
    const role = String(store.getters.currentRole || "").toLowerCase();
    const status = String(store.getters.currentStatus || "");
    const isPortalCustomer =
      role === "rosie customer" && status.startsWith("ACTIVE");
    const isPortalClient =
      role === "rosie client" && status.startsWith("ACTIVE");
    const isPortalUser = isPortalCustomer || isPortalClient;
    const portalHome = isPortalClient ? "portal-client-home" : "portal-home";
    if (
      isPortalUser &&
      !to.meta.portal &&
      !PORTAL_USER_ALLOWED.has(String(to.name))
    ) {
      return { name: portalHome };
    }
    // Clients only see their own part of the portal, customers (and view-as) only theirs
    if (isPortalClient && to.meta.portal && !to.meta.client) {
      return { name: "portal-client-home" };
    }
    if (!isPortalClient && to.meta.client) {
      return { name: "portal-home" };
    }
    // Admin "View as customer" tab: only the portal (view only); the session is this tab's alone
    const viewingAs =
      role === "admin" && !!window.sessionStorage.getItem("bender-view-as");
    // Already signed in (e.g. reopened the site): don't ask to sign in again, go home instead.
    // An expired token is cleared, and the sign-in page shows as usual.
    if (to.name === "sign-in") {
      if (JwtService.hasLiveToken()) {
        return isPortalUser || viewingAs
          ? { name: portalHome }
          : { name: "provisioning-clients" };
      }
      store.commit(Mutations.PURGE_AUTH);
      return;
    }
    if (viewingAs && !to.meta.portal && to.name !== "view-as") {
      return { name: "portal-home" };
    }
    if (!isPortalUser && !viewingAs && to.meta.portal) {
      return { path: "/" };
    }
  } else if (to.meta.portal) {
    return { name: "sign-in" };
  }
});

router.afterEach(() => {
  store.dispatch(Actions.RENEW_AUTH);
});

export default router;
